import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  Bot,
  Zap,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Settings,
  Activity,
  BarChart3,
  Lightbulb,
  Play,
  Pause,
  Shield
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AIMarketingAgent } from '../lib/ai-marketing-agent';

interface AIAgentConfig {
  autonomy_mode: 'supervised' | 'semi_autonomous' | 'fully_autonomous';
  auto_approve_threshold: number;
}

interface Decision {
  id: string;
  action_type: string;
  reasoning: string;
  confidence_score: number;
  status: string;
  created_at: string;
  approved_at?: string;
}

interface Action {
  id: string;
  action_type: string;
  details: any;
  status: string;
  result?: any;
  created_at: string;
  completed_at?: string;
}

export default function AIAgentMonitorPage() {
  const [config, setConfig] = useState<AIAgentConfig>({
    autonomy_mode: 'supervised',
    auto_approve_threshold: 0.8
  });
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load config
      const { data: configData } = await supabase
        .from('ai_agent_config')
        .select('*')
        .single();

      if (configData) {
        setConfig(configData);
      }

      // Load recent decisions
      const { data: decisionsData } = await supabase
        .from('ai_agent_decisions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (decisionsData) {
        setDecisions(decisionsData);
      }

      // Load recent actions
      const { data: actionsData } = await supabase
        .from('ai_agent_actions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);

      if (actionsData) {
        setActions(actionsData);
      }
    } catch (error) {
      console.error('Error loading AI agent data:', error);
      toast.error('Failed to load AI agent data');
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = async (updates: Partial<AIAgentConfig>) => {
    try {
      setSaving(true);
      
      // Only update the specific fields that changed
      const { error } = await supabase
        .from('ai_agent_config')
        .update(updates)
        .eq('id', '4cd358f7-6d79-4594-8710-26f75cd69102');

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Update local state
      setConfig(prev => ({ ...prev, ...updates }));
      toast.success('Configuration updated');
      
      // Reload to get fresh data
      await loadData();
    } catch (error: any) {
      console.error('Error updating config:', error);
      toast.error(`Failed to update configuration: ${error.message || 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const approveDecision = async (decisionId: string) => {
    try {
      const { error } = await supabase
        .from('ai_agent_decisions')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString()
        })
        .eq('id', decisionId);

      if (error) throw error;

      toast.success('Decision approved');
      loadData();
    } catch (error) {
      console.error('Error approving decision:', error);
      toast.error('Failed to approve decision');
    }
  };

  const rejectDecision = async (decisionId: string) => {
    try {
      const { error } = await supabase
        .from('ai_agent_decisions')
        .update({
          status: 'rejected',
          approved_at: new Date().toISOString()
        })
        .eq('id', decisionId);

      if (error) throw error;

      toast.success('Decision rejected');
      loadData();
    } catch (error) {
      console.error('Error rejecting decision:', error);
      toast.error('Failed to reject decision');
    }
  };

  const runAgentNow = async () => {
    try {
      setRunning(true);
      toast.loading('Running AI agent analysis...', { id: 'agent-run' });

      // Create agent instance and run analysis cycle
      const agent = new AIMarketingAgent({
        company_id: '4cd358f7-6d79-4594-8710-26f75cd69102',
        autonomy_mode: config.autonomy_mode,
        auto_approve_threshold: config.auto_approve_threshold,
        daily_action_limit: 50,
        monitoring_frequency_hours: 24,
        alert_email_addresses: [],
        primary_goals: []
      });

      await agent.runAnalysisCycle();

      toast.success('AI agent analysis complete!', { id: 'agent-run' });
      
      // Reload data to show new decisions/actions
      await loadData();
    } catch (error: any) {
      console.error('Error running AI agent:', error);
      toast.error(`Failed to run AI agent: ${error.message || 'Unknown error'}`, { id: 'agent-run' });
    } finally {
      setRunning(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case 'rejected':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading AI agent data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Bot className="w-8 h-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">AI Marketing Agent Control</h1>
        </div>
        <p className="text-gray-600">
          Monitor and control the autonomous AI marketing system
        </p>
      </div>

      {/* Quick Actions Bar */}
      <div className="card mb-8 bg-gradient-to-r from-purple-50 to-blue-50 border-2 border-purple-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Manual Controls</h3>
            <p className="text-sm text-gray-600">
              Trigger an immediate AI analysis cycle to generate new decisions and actions
            </p>
          </div>
          <button
            onClick={runAgentNow}
            disabled={running}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
              running
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {running ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Running...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Run Agent Now
              </>
            )}
          </button>
        </div>
      </div>

      {/* Configuration Panel */}
      <div className="card mb-8">
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-6 h-6 text-gray-600" />
          <h2 className="text-xl font-bold text-gray-900">Agent Configuration</h2>
        </div>

        <div className="space-y-6">
          {/* Autonomy Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Autonomy Mode
            </label>
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => updateConfig({ autonomy_mode: 'supervised' })}
                disabled={saving}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.autonomy_mode === 'supervised'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Shield className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <div className="font-semibold">Supervised</div>
                <div className="text-sm text-gray-600 mt-1">
                  All decisions require approval
                </div>
              </button>

              <button
                onClick={() => updateConfig({ autonomy_mode: 'semi_autonomous' })}
                disabled={saving}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.autonomy_mode === 'semi_autonomous'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Zap className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                <div className="font-semibold">Semi-Autonomous</div>
                <div className="text-sm text-gray-600 mt-1">
                  Auto-approve high confidence
                </div>
              </button>

              <button
                onClick={() => updateConfig({ autonomy_mode: 'fully_autonomous' })}
                disabled={saving}
                className={`p-4 rounded-lg border-2 transition-all ${
                  config.autonomy_mode === 'fully_autonomous'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Bot className="w-6 h-6 mx-auto mb-2 text-green-600" />
                <div className="font-semibold">Fully Autonomous</div>
                <div className="text-sm text-gray-600 mt-1">
                  Fully independent operation
                </div>
              </button>
            </div>
          </div>

          {/* Auto-Approve Threshold */}
          {config.autonomy_mode === 'semi_autonomous' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auto-Approve Confidence Threshold: {(config.auto_approve_threshold * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min="0.5"
                max="1"
                step="0.05"
                value={config.auto_approve_threshold}
                onChange={(e) => updateConfig({ auto_approve_threshold: parseFloat(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-600 mt-1">
                <span>50% (Less restrictive)</span>
                <span>100% (More restrictive)</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* What It Does - Info Panel */}
      <div className="card mb-8 bg-blue-50 border-2 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <Lightbulb className="w-6 h-6 text-blue-600 mt-1" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-3">What This AI Agent Does</h3>
            
            <div className="space-y-4 text-gray-700">
              <p>
                The AI Marketing Agent continuously monitors your business performance across all digital channels 
                and autonomously optimizes your marketing efforts to maximize ROI. It analyzes data from multiple 
                sources and makes intelligent decisions to improve your online presence.
              </p>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Data Sources Monitored:</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Keyword rankings and search volume (DataForSEO)</li>
                  <li>Competitor positioning and market share</li>
                  <li>Website performance and Core Web Vitals</li>
                  <li>Google Business Profile engagement</li>
                  <li>Social media performance metrics</li>
                  <li>Email campaign effectiveness</li>
                  <li>Lead scoring and conversion rates</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Actions It Can Take:</h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>Content Creation:</strong> Generate and publish WordPress blog posts optimized for SEO</li>
                  <li><strong>Social Posts:</strong> Create and schedule Google Business Profile, Facebook, and Instagram posts</li>
                  <li><strong>SEO Optimization:</strong> Update meta descriptions, titles, and schema markup on WordPress sites</li>
                  <li><strong>Workflow Triggers:</strong> Launch automated campaigns based on customer behavior</li>
                  <li><strong>Email Campaigns:</strong> Send targeted email campaigns to specific segments</li>
                  <li><strong>SMS Outreach:</strong> Deploy SMS campaigns for time-sensitive promotions</li>
                  <li><strong>Competitive Response:</strong> Adjust strategies based on competitor movements</li>
                  <li><strong>Performance Reports:</strong> Generate weekly scorecards with insights and recommendations</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Operating Modes:</h4>
                <div className="space-y-2 ml-2">
                  <div>
                    <span className="font-semibold">Supervised:</span> All decisions require manual approval before execution. 
                    Best for getting started or when you want full control.
                  </div>
                  <div>
                    <span className="font-semibold">Semi-Autonomous:</span> High-confidence decisions (above your threshold) 
                    are auto-approved and executed. Lower-confidence decisions still require approval.
                  </div>
                  <div>
                    <span className="font-semibold">Fully Autonomous:</span> AI operates independently within your set 
                    budget and action limits. All decisions are executed automatically with full transparency.
                  </div>
                </div>
              </div>

              <div className="bg-blue-100 border border-blue-200 rounded-lg p-3">
                <p className="text-sm">
                  <strong>💡 Pro Tip:</strong> Start in Supervised mode to review the AI's decision-making patterns. 
                  Once comfortable, switch to Semi-Autonomous with an 80% threshold, then gradually increase autonomy 
                  as you build trust in the system.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Decisions */}
      <div className="card mb-8">
        <div className="flex items-center gap-3 mb-6">
          <AlertCircle className="w-6 h-6 text-amber-600" />
          <h2 className="text-xl font-bold text-gray-900">Pending Decisions</h2>
          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">
            {decisions.filter(d => d.status === 'pending').length}
          </span>
        </div>

        {decisions.filter(d => d.status === 'pending').length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No pending decisions</p>
          </div>
        ) : (
          <div className="space-y-4">
            {decisions
              .filter(d => d.status === 'pending')
              .map((decision) => (
                <div key={decision.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-gray-900">{decision.action_type}</span>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                          {(decision.confidence_score * 100).toFixed(0)}% confidence
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{decision.reasoning}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => approveDecision(decision.id)}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => rejectDecision(decision.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(decision.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Recent Actions */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <Activity className="w-6 h-6 text-green-600" />
          <h2 className="text-xl font-bold text-gray-900">Recent Actions</h2>
        </div>

        {actions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Activity className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p>No actions yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {actions.map((action) => (
              <div key={action.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex-shrink-0">
                  {getStatusIcon(action.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">{action.action_type}</span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      action.status === 'completed'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {action.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    {new Date(action.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
